import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { applyTheme, DARK_MODE_QUERY, parseThemePreference, readThemePreference, ResolvedTheme, THEME_STORAGE_KEY, ThemePreference } from "../utils/theme";

interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readThemePreference);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.(DARK_MODE_QUERY).matches ?? false);
  const resolvedTheme: ResolvedTheme = preference === "auto" ? (systemDark ? "dark" : "light") : preference;

  useLayoutEffect(() => applyTheme(resolvedTheme), [resolvedTheme]);

  useEffect(() => {
    const media = window.matchMedia?.(DARK_MODE_QUERY);
    const updateSystem = () => setSystemDark(media?.matches ?? false);
    updateSystem();
    media?.addEventListener("change", updateSystem);
    const syncPreference = (event: StorageEvent) => {
      if (event.storageArea && event.storageArea !== window.localStorage) return;
      if (event.key === THEME_STORAGE_KEY || event.key === null) {
        setPreferenceState(event.key === null ? "auto" : parseThemePreference(event.newValue));
      }
    };
    window.addEventListener("storage", syncPreference);
    return () => {
      media?.removeEventListener("change", updateSystem);
      window.removeEventListener("storage", syncPreference);
    };
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch { /* The theme still works when storage is unavailable. */ }
  }, []);
  const value = useMemo(() => ({ preference, resolvedTheme, setPreference }), [preference, resolvedTheme, setPreference]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
}
